// Workflow Metrics Step - Select financial metrics and ratios
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useMemo } from 'react';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { WorkflowNavigation } from '@/components/workflow/WorkflowNavigation';
import { MetricsSelector } from '@/components/metrics/MetricsSelector';
import { OffBalanceSheetDialog } from '@/components/metrics/OffBalanceSheetDialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Calculator, Plus, CheckCircle, ChevronRight, AlertTriangle } from 'lucide-react';
import { CustomRatio, FINANCIAL_METRICS } from '@creditnova/shared';
import { api } from '@/lib/api-client';

export const Route = createFileRoute('/workflow/_layout/metrics')({
  component: MetricsStep,
});

interface OffBalanceSheetItem {
  id: string;
  name: string;
  value: string;
  description?: string;
}

function MetricsStep() {
  const { setCurrentStep, updateData, data, markStepComplete } = useWorkflow();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Report is in-flight or done — page is view-only to prevent misleading edits
  const isReadonly = data.workflowState === 'generating' || data.workflowState === 'complete';

  const [showMetricsDialog, setShowMetricsDialog] = useState(false);
  const [showOffBalanceSheetDialog, setShowOffBalanceSheetDialog] = useState(false);
  const [isComplete, setIsComplete] = useState(
    data.selectedMetrics.length > 0 || data.customRatios.length > 0
  );

  // Sync workflow context
  useEffect(() => {
    setCurrentStep('metrics');
  }, [setCurrentStep]);

  const handleSaveMetrics = (metrics: string[], customRatios: CustomRatio[]) => {
    updateData({
      selectedMetrics: metrics,
      customRatios: customRatios, // Sync the actual formula objects to context
    });

    // Sync metrics + custom ratios to backend (fire-and-forget)
    if (data.workflowId) {
      api.workflows.update(data.workflowId, {
        selectedMetrics: metrics,
        customRatios: customRatios,
      }).catch((err) =>
        console.warn('Failed to sync selectedMetrics/customRatios to backend:', err)
      );
    }

    markStepComplete('metrics');
    setIsComplete(true);

    toast({
      title: 'Metrics Selected',
      description: `${metrics.length} metric${metrics.length !== 1 ? 's' : ''} saved with ${customRatios.length} custom ratio${customRatios.length !== 1 ? 's' : ''}`,
    });
  };

  const handleSaveOffBalanceSheet = (items: OffBalanceSheetItem[]) => {
    // Convert items array to record format for workflow data
    const records: Record<string, string> = {};
    items.forEach((item) => {
      records[item.name] = item.value;
    });

    updateData({
      offBalanceSheetRecords: records,
    });

    // Sync off-balance-sheet records to backend (fire-and-forget)
    if (data.workflowId) {
      api.workflows.update(data.workflowId, {
        offBalanceSheetRecords: records,
      }).catch((err) =>
        console.warn('Failed to sync offBalanceSheetRecords to backend:', err)
      );
    }

    toast({
      title: 'Off-Balance Sheet Records Saved',
      description: `${items.length} record${items.length !== 1 ? 's' : ''} added for custom ratio calculations`,
    });
  };

  // Consolidating available fields for formula builder to include off balance sheet records and extracted financial data fields
  // So that they are availble in metric ratios formulas
  // Uses union of fields from ALL financial extractions (multi-doc flow) or single financialData (legacy)
  const availableFields = useMemo(() => {
    const fields = new Set<string>();

    // Multi-document flow: union of all financial extraction fields
    if (data.financialExtractions && data.financialExtractions.length > 0) {
      for (const ext of data.financialExtractions) {
        const fd = ext.data?.data;
        if (fd) {
          if (fd.balanceSheet) Object.keys(fd.balanceSheet).forEach(k => fields.add(k));
          if (fd.incomeStatement) Object.keys(fd.incomeStatement).forEach(k => fields.add(k));
          if (fd.cashFlow) Object.keys(fd.cashFlow).forEach(k => fields.add(k));
        }
      }
    } else {
      // Legacy single-document flow
      const fd = data.financialData?.data;
      if (fd) {
        if (fd.balanceSheet) Object.keys(fd.balanceSheet).forEach(k => fields.add(k));
        if (fd.incomeStatement) Object.keys(fd.incomeStatement).forEach(k => fields.add(k));
        if (fd.cashFlow) Object.keys(fd.cashFlow).forEach(k => fields.add(k));
      }
    }

    Object.keys(data.offBalanceSheetRecords || {}).forEach(k => fields.add(k));
    return Array.from(fields);
  }, [data.financialData, data.financialExtractions, data.offBalanceSheetRecords]);

  // Convert records back to items for the dialog
  const offBalanceSheetItems: OffBalanceSheetItem[] = Object.entries(
    data.offBalanceSheetRecords
  ).map(([name, value], index) => ({
    id: `item-${index}`,
    name,
    value: String(value ?? ''),
  }));

  const totalMetrics = data.selectedMetrics.length;
  const offBalanceSheetCount = Object.keys(data.offBalanceSheetRecords).length;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Calculator className="w-10 h-10 text-accent-500" />
          <h1 className="text-4xl font-bold">Financial Metrics & Ratios</h1>
        </div>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Select the financial ratios and metrics you want to include in your analysis.
          You can also add custom ratios and off-balance sheet items.
        </p>
      </div>

      {/* Readonly banner — shown when report generation is in progress */}
      {isReadonly && (
        <Card className="p-4 rounded-2xl mb-6 border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Report generation is {data.workflowState === 'complete' ? 'complete' : 'in progress'} — metric selections are locked and view-only. Changes made here will not affect the report.
            </p>
          </div>
        </Card>
      )}

      <div className="space-y-8">
        {/* Off-Balance Sheet Card */}
        <Card className="p-8 border-2 rounded-2xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold">Off-Balance Sheet Items</h3>
              <p className="text-muted-foreground">
                {offBalanceSheetCount > 0
                  ? `${offBalanceSheetCount} item${offBalanceSheetCount !== 1 ? 's' : ''} added`
                  : 'No off-balance sheet items added'}
              </p>
            </div>
            <Button variant="outline" onClick={() => setShowOffBalanceSheetDialog(true)} disabled={isReadonly}>
              <Plus className="w-4 h-4 mr-2" />
              Add Items
            </Button>
          </div>

          {/* Display off-balance sheet items */}
          {offBalanceSheetCount > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(data.offBalanceSheetRecords).map(([key, value]) => (
                <div key={key} className="p-3 bg-accent/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">{key}</p>
                  <p className="font-semibold">{String(value ?? '')}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Metrics Selection Card */}
        <Card className="p-8 border-2 rounded-2xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              {isComplete && (
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
              )}
              <div>
                <h3 className="text-xl font-semibold">Selected Metrics</h3>
                <p className="text-muted-foreground">
                  {totalMetrics > 0
                    ? `${totalMetrics} metric${totalMetrics !== 1 ? 's' : ''} selected`
                    : 'No metrics selected yet'}
                </p>
              </div>
            </div>
            <Button onClick={() => setShowMetricsDialog(true)} disabled={isReadonly}>
              {totalMetrics > 0 ? 'Edit Metrics' : 'Select Metrics'}
            </Button>
          </div>

          {/* Display selected metrics */}
          {totalMetrics > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.selectedMetrics.map((id) => {
                // Check if it's a standard metric
                const standardMetric = FINANCIAL_METRICS.find(m => m.id === id);
                // Check if it's a custom metric
                const customMetric = data.customRatios.find(r => r.id === id);

                const displayName = customMetric ? customMetric.name : (standardMetric?.name || id);
                const isCustom = !!customMetric;

                return (
                  <span
                    key={id}
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isCustom 
                        ? "bg-purple-500/10 text-purple-500" 
                        : "bg-accent-500/10 text-accent-500"
                    }`}
                  >
                    {displayName} {isCustom && "(Custom)"}
                  </span>
                );
              })}
            </div>
          )}
        </Card>

        {/* Info Card */}
        <Card className="p-6 border-2 rounded-2xl bg-primary/5">
          <p className="text-center text-muted-foreground">
            <strong>Tip:</strong> Selected metrics will be calculated from your financial
            statement data and included in the final credit analysis report.
          </p>
        </Card>
      </div>

      {/* Metrics Selector Dialog */}
      <MetricsSelector
        open={showMetricsDialog}
        onOpenChange={setShowMetricsDialog}
        selectedMetrics={data.selectedMetrics}
        initialCustomRatios={data.customRatios}
        onSave={handleSaveMetrics}
        availableFields={availableFields}
      />

      {/* Off-Balance Sheet Dialog */}
      <OffBalanceSheetDialog
        open={showOffBalanceSheetDialog}
        onOpenChange={setShowOffBalanceSheetDialog}
        items={offBalanceSheetItems}
        onSave={handleSaveOffBalanceSheet}
      />

      {/* Navigation — hidden when readonly (generating/complete) to prevent state regression */}
      {!isReadonly && (
        (data.workflowState === 'financial_review' || data.workflowState === 'metrics_review') ? (
          // Concurrent flow: navigate back to upload.tsx for remaining doc processing.
          // We use a standalone button instead of WorkflowNavigation to avoid
          // nextStep() firing and overriding our navigate() call.
          <div className="flex justify-end mt-8">
            <Button
              size="lg"
              onClick={async () => {
                markStepComplete('metrics');
                updateData({ workflowState: 'processing_remaining' });
                // Await the backend PATCH so the state is persisted before
                // the user can refresh. Ensures resume lands on the correct step.
                if (data.workflowId) {
                  try {
                    await api.workflows.update(data.workflowId, { workflowState: 'processing_remaining' });
                  } catch (err) {
                    console.warn('Failed to sync workflowState=processing_remaining to backend:', err);
                  }
                }
                navigate({ to: '/workflow/generate' });
              }}
            >
              {totalMetrics === 0 ? "Skip Metrics" : "Continue"}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : (
          // Legacy flow: let WorkflowNavigation handle step progression normally
          <WorkflowNavigation
            isNextDisabled={false}
            nextLabel={totalMetrics === 0 ? "Skip Metrics" : "Continue"}
            onNext={() => {
              markStepComplete('metrics');
            }}
          />
        )
      )}
    </div>
  );
}
